// Tenant (league) endpoints: /api/v1/admin/platform/leagues* (API_CONTRACT.md).
// Every write answers with the whole tenant (`{ tenant }`).

import { apiFetch } from './client'
import type {
  AccountPatch,
  AddonsPatch,
  AddTenantAdminRequest,
  AttributionPatch,
  CreateTenantRequest,
  CreateTenantResponse,
  CustomDomainStatus,
  DomainChangeResponse,
  ImpersonationResponse,
  LeagueRole,
  RegistrationFeeRequest,
  SportBootstrapResult,
  SportCatalogEntry,
  SubdomainCheck,
  TenantAdmin,
  TenantDetail,
  TenantListParams,
  TenantListResponse,
  TenantPatch,
  TenantResponse,
  TenantSport,
  TenantUsage,
  TicketingFeeRequest,
} from './types'

const BASE = '/admin/platform/leagues'
export const DEFAULT_PAGE_SIZE = 25

// ─── List ───────────────────────────────────────────────────────────────────

/** Search / filter / sort / page tenants on the server. */
export function listTenants(params: TenantListParams = {}, signal?: AbortSignal): Promise<TenantListResponse> {
  return apiFetch<TenantListResponse>(BASE, {
    query: {
      q: params.q?.trim() || undefined,
      status: params.status,
      package_key: params.package_key,
      addon: params.addon,
      source: params.source,
      sort: params.sort,
      page: Math.max(1, params.page ?? 1),
      page_size: Math.min(100, Math.max(1, params.page_size ?? DEFAULT_PAGE_SIZE)),
    },
    signal,
  })
}

// ─── Single tenant ──────────────────────────────────────────────────────────

export async function getTenant(id: number, signal?: AbortSignal): Promise<TenantDetail> {
  const r = await apiFetch<TenantResponse>(`${BASE}/${id}`, { signal })
  return r.tenant
}

// ─── Create (one transaction) ───────────────────────────────────────────────

export function checkSubdomain(subdomain: string, signal?: AbortSignal): Promise<SubdomainCheck> {
  return apiFetch(`${BASE}/check-subdomain`, { query: { subdomain }, signal })
}

/** POST /platform/leagues: league, sport, trial and first admin in one transaction. */
export function createTenant(body: CreateTenantRequest): Promise<CreateTenantResponse> {
  return apiFetch(BASE, { method: 'POST', body })
}

// ─── Per-field writes ───────────────────────────────────────────────────────

export async function updateTenant(id: number, patch: TenantPatch): Promise<TenantDetail> {
  const r = await apiFetch<TenantResponse>(`${BASE}/${id}`, { method: 'PATCH', body: patch })
  return r.tenant
}

export function changeSubdomain(id: number, subdomain: string): Promise<DomainChangeResponse> {
  return apiFetch(`${BASE}/${id}/subdomain`, { method: 'PUT', body: { subdomain } })
}

export function changeCustomDomain(id: number, customDomain: string | null): Promise<DomainChangeResponse> {
  return apiFetch(`${BASE}/${id}/custom-domain`, { method: 'PUT', body: { custom_domain: customDomain } })
}

export async function updateAccount(id: number, patch: AccountPatch): Promise<{ tenant: TenantDetail; warning: string | null }> {
  const r = await apiFetch<TenantResponse & { details?: { warning?: string }; warning?: string }>(`${BASE}/${id}/account`, {
    method: 'PATCH',
    body: patch,
  })
  return { tenant: r.tenant, warning: r.details?.warning ?? r.warning ?? null }
}

/** Merges: only the keys sent change (#324). */
export async function updateAddons(id: number, patch: AddonsPatch): Promise<TenantDetail> {
  const r = await apiFetch<TenantResponse>(`${BASE}/${id}/addons`, { method: 'PATCH', body: patch })
  return r.tenant
}

export async function updateAttribution(id: number, patch: AttributionPatch): Promise<TenantDetail> {
  const r = await apiFetch<TenantResponse>(`${BASE}/${id}/attribution`, { method: 'PATCH', body: patch })
  return r.tenant
}

async function putFee(id: number, kind: 'ticketing' | 'registration', body: TicketingFeeRequest | RegistrationFeeRequest): Promise<TenantDetail> {
  const r = await apiFetch<TenantResponse>(`${BASE}/${id}/fees/${kind}`, { method: 'PUT', body })
  return r.tenant
}

export function setTicketingFee(id: number, body: TicketingFeeRequest): Promise<TenantDetail> {
  return putFee(id, 'ticketing', body)
}

export function setRegistrationFee(id: number, body: RegistrationFeeRequest): Promise<TenantDetail> {
  return putFee(id, 'registration', body)
}

export async function suspendTenant(id: number, reason: string): Promise<TenantDetail> {
  const r = await apiFetch<TenantResponse>(`${BASE}/${id}/suspend`, { method: 'POST', body: { reason } })
  return r.tenant
}

export async function reinstateTenant(id: number): Promise<TenantDetail> {
  const r = await apiFetch<TenantResponse>(`${BASE}/${id}/reinstate`, { method: 'POST', body: {} })
  return r.tenant
}

/** DELETE with { confirm_subdomain } (the server checks it: 400 confirmation_mismatch). */
export async function deleteTenant(id: number, confirmSubdomain: string): Promise<void> {
  await apiFetch(`${BASE}/${id}`, { method: 'DELETE', body: { confirm_subdomain: confirmSubdomain } })
}

export function impersonationLink(id: number): Promise<ImpersonationResponse> {
  return apiFetch(`${BASE}/${id}/impersonate`, { method: 'POST', body: {} })
}

// ─── Domains ───────────────────────────────────────────

export function customDomainStatus(id: number, signal?: AbortSignal): Promise<CustomDomainStatus> {
  return apiFetch(`${BASE}/${id}/custom-domain/status`, { signal })
}

export function registerCustomDomain(id: number): Promise<{ league_id: number; already_registered: boolean; cf: unknown }> {
  return apiFetch(`${BASE}/${id}/custom-domain/register`, { method: 'POST' })
}

// ─── Billing ─────────────────────────────────────────────────────


// ─── Usage ───────────────────────────────────────────────────────

export function getUsage(id: number, signal?: AbortSignal): Promise<TenantUsage> {
  return apiFetch(`${BASE}/${id}/usage`, { signal })
}

// ─── Sports ────────────────────────────────────────────

export async function listSports(id: number, signal?: AbortSignal): Promise<TenantSport[]> {
  const r = await apiFetch<{ sports: TenantSport[] }>(`${BASE}/${id}/sports`, { signal })
  return r.sports
}

export function activateSport(id: number, sportId: string): Promise<SportBootstrapResult> {
  return apiFetch(`${BASE}/${id}/sports/${encodeURIComponent(sportId)}`, { method: 'POST' })
}

export function archiveSport(id: number, sportId: string): Promise<void> {
  return apiFetch(`${BASE}/${id}/sports/${encodeURIComponent(sportId)}/archive`, { method: 'POST' })
}

export function restoreSport(id: number, sportId: string): Promise<void> {
  return apiFetch(`${BASE}/${id}/sports/${encodeURIComponent(sportId)}/restore`, { method: 'POST' })
}

/** Sports the wizard offers when GET /public/sports can't be reached. */
export const FALLBACK_SPORTS: SportCatalogEntry[] = [
  { id: 'basketball', display_name: 'Basketball' },
  { id: 'soccer', display_name: 'Soccer' },
]

/** Every registered sport pack (GET /public/sports, which needs no tenant). */
export async function sportCatalog(signal?: AbortSignal): Promise<SportCatalogEntry[]> {
  const r = await apiFetch<{ sports: Array<{ id: string; displayName?: string; display_name?: string }> }>('/public/sports', { signal, auth: false })
  const list = (r?.sports ?? []).map((s) => ({ id: s.id, display_name: s.display_name ?? s.displayName ?? s.id }))
  return list.length ? list : FALLBACK_SPORTS
}

// ─── People ─────────────────────────────────────────────────────────────────

export function listTenantAdmins(id: number, signal?: AbortSignal): Promise<{ admins: TenantAdmin[] }> {
  return apiFetch(`${BASE}/${id}/admins`, { signal })
}

/** POST …/admins by email; a new account needs `password` (#621). */
export function addTenantAdmin(id: number, body: AddTenantAdminRequest): Promise<{ admin: TenantAdmin; created_user: boolean }> {
  return apiFetch(`${BASE}/${id}/admins`, { method: 'POST', body })
}

export async function changeTenantAdminRole(id: number, userId: number, role: LeagueRole): Promise<TenantAdmin> {
  const r = await apiFetch<{ admin: TenantAdmin }>(`${BASE}/${id}/admins/${userId}`, { method: 'PATCH', body: { role } })
  return r.admin
}

/** PUT …/admins/:userId/password: a super-admin sets a member's sign-in password (#621). */
export async function setTenantAdminPassword(id: number, userId: number, password: string): Promise<TenantAdmin> {
  const r = await apiFetch<{ admin: TenantAdmin }>(`${BASE}/${id}/admins/${userId}/password`, { method: 'PUT', body: { password } })
  return r.admin
}

export function removeTenantAdmin(id: number, userId: number): Promise<void> {
  return apiFetch(`${BASE}/${id}/admins/${userId}`, { method: 'DELETE' })
}
